// Flat product navigation; each section keeps its views inside the page.
import { Activity, Bot, CreditCard, Database, FlaskConical, House, Laptop, Layers, Server, Settings, ShieldCheck, Sparkles, Users, type LucideIcon } from "lucide-react";

type Level = "project" | "org";

export interface NavTab {
  id: string;
  label: string;
  level: Level;
  path: string;
  /** Segments that make this tab active (defaults to the first segment of `path`). */
  segments: string[];
  servedKind?: string;
  /**
   * A list view picked by one search param (`?view=parallel`). The `fallback` view is also the one shown when the
   * param is absent. Links include the param so only one query-backed tab is active.
   */
  query?: { key: string; value: string; fallback?: boolean };
}

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Where the item links: a project page or an org page. */
  level: Level;
  path: string;
  /** Every segment that belongs to the section (its tabs' segments included). */
  segments: string[];
  /** The `g` chord key (ui-spec §1.4). */
  chord?: string;
  /** Hidden for principals without this scope (only Billing, ui-spec §2.3 Roles). */
  requiresScope?: string;
  /** The section's views, shown as tabs under the page title. */
  tabs?: NavTab[];
  /** The section's list views, nested under it in the rail. */
  children?: NavTab[];
  servedKind?: string;
  /** Secondary items sit below the rail's divider. */
  secondary?: boolean;
  group?: "resources";
}

const tab = (id: string, label: string, level: Level, path: string, segments: string[] = [path], servedKind?: string): NavTab => ({
  id,
  label,
  level,
  path,
  segments,
  ...(servedKind ? { servedKind } : {}),
});

export const NAV: NavItem[] = [
  { id: "overview", label: "Overview", icon: House, level: "project", path: "overview", segments: ["overview"] },
  {
    id: "instances", label: "Instances", icon: Server, level: "project", path: "instances",
    segments: ["instances", "compute"], chord: "r",
  },
  {
    id: "workspaces",
    label: "Workspaces",
    icon: Laptop,
    level: "project",
    path: "workspaces",
    segments: ["workspaces"],
    chord: "w",
  },
  {
    // Training: fine-tuning and RL runs, environments with their graders, and training templates.
    id: "environments",
    label: "Training",
    icon: FlaskConical,
    level: "project",
    path: "environments",
    segments: ["environments", "trainingruntimes", "trainingjobs"],
    chord: "e",
  },
  {
    id: "inference",
    label: "Inference",
    icon: Sparkles,
    level: "project",
    path: "inference",
    segments: ["inference", "inferenceendpoints", "models", "functions", "apps"],
    chord: "i",
  },
  {
    id: "agents",
    label: "Agents",
    icon: Bot,
    level: "project",
    path: "agents",
    segments: ["sandboxes", "agents", "agentruns", "agentgroups"],
    chord: "a",
    tabs: [
      { ...tab("agentruns", "Runs", "project", "agents", ["agentruns"]), query: { key: "tab", value: "runs", fallback: true } },
      { ...tab("agents", "Definitions", "project", "agents"), query: { key: "tab", value: "agents" } },
      { ...tab("agentgroups", "Groups", "project", "agents", ["agentgroups"]), query: { key: "tab", value: "groups" } },
      tab("sandboxes", "Sandboxes", "project", "sandboxes"),
    ],
  },
  {
    id: "runs", label: "Batch jobs", icon: Activity, level: "project", path: "runs", group: "resources",
    segments: ["runs", "jobs", "pipelines", "sweeps", "attempts", "processes", "functioncalls"],
  },
  { id: "assets", label: "Models & datasets", icon: Layers, level: "project", path: "assets", segments: ["assets"], group: "resources" },
  { id: "reservedhosts", label: "Reserved compute", icon: ShieldCheck, level: "project", path: "reservedhosts", segments: ["reservedhosts"], group: "resources" },
  {
    // Bring your own compute: Pools, enrolled Nodus nodes and connected cloud accounts.
    id: "byocompute",
    label: "BYOC",
    group: "resources",
    icon: Server,
    level: "project",
    path: "pools",
    segments: ["pools", "poolactions", "enrollmenttokens", "infrastructure"],
    chord: "c",
  },
  {
    id: "storage",
    label: "Storage",
    icon: Database,
    level: "project",
    path: "volumes",
    segments: ["volumes", "secrets", "images"],
    chord: "o",
    group: "resources",
    tabs: [
      tab("volumes", "Volumes", "project", "volumes"),
      tab("secrets", "Secrets", "project", "secrets"),
      tab("images", "Images", "project", "images"),
    ],
  },
  {
    // The people in the org and how they are grouped: members with their roles, seats and spend, projects (one per
    // team, each with its own spend limit), pending invites, and the company domains that let colleagues join.
    id: "team",
    label: "Team",
    icon: Users,
    level: "org",
    path: "team/members",
    segments: ["team"],
    chord: "t",
    secondary: true,
    tabs: [
      tab("members", "Members", "org", "team/members", ["team/members"]),
      tab("projects", "Projects", "org", "team/projects", ["team/projects"]),
      tab("invites", "Invites", "org", "team/invites", ["team/invites"]),
      tab("domains", "Company domains", "org", "team/domains", ["team/domains"]),
    ],
  },
  {
    id: "billing",
    label: "Usage & billing",
    icon: CreditCard,
    level: "org",
    path: "billing/overview",
    segments: ["billing"],
    chord: "b",
    requiresScope: "billing:read",
    secondary: true,
    tabs: [
      tab("overview", "Balance", "org", "billing/overview", ["billing/overview", "billing/credits"]),
      tab("usage", "Usage", "org", "billing/usage", ["billing/usage"]),
      tab("transactions", "Transactions", "org", "billing/transactions", ["billing/transactions", "billing/receipts"]),
      tab("budgets", "Budgets", "org", "billing/budgets", ["billing/budgets", "billing/notifications"]),
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    level: "org",
    path: "settings/general",
    segments: ["settings", "serviceaccounts", "connections", "integrations"],
    chord: "x",
    secondary: true,
    tabs: [
      tab("general", "General", "org", "settings/general", ["settings/general"]),
      tab("apikeys", "API keys", "org", "settings/apikeys", ["settings/apikeys"]),
      tab("serviceaccounts", "Service accounts", "project", "serviceaccounts"),
      tab("webhooks", "Webhooks", "org", "settings/webhooks", ["settings/webhooks"]),
      tab("quotas", "Quotas", "org", "settings/quotas", ["settings/quotas"]),
      tab("compute", "Compute", "org", "settings/compute", ["settings/compute"]),
      tab("audit", "Audit log", "org", "settings/audit", ["settings/audit"]),
      tab("connections", "Connections", "project", "connections", ["connections", "integrations"]),
    ],
  },
];

/** Org-level first segments; everything else after the org is a project. */
export const ORG_SEGMENTS = new Set(["settings", "team", "billing", "integrations", "infrastructure", "support", "spot"]);

function href(level: Level, path: string, org: string, project: string): string {
  const o = encodeURIComponent(org);
  return level === "org" ? `/${o}/${path}` : `/${o}/${encodeURIComponent(project)}/${path}`;
}

export function navHref(item: NavItem | NavTab, org: string, project: string): string {
  const q = "query" in item && item.query ? `?${item.query.key}=${encodeURIComponent(item.query.value)}` : "";
  return href(item.level, item.path, org, project) + q;
}

/** The path after the org (org pages) or after the project (project pages), decoded. */
function rest(pathname: string): string | undefined {
  const parts = pathname.split("/").filter(Boolean).map(decodeURIComponent);
  if (parts.length < 2) return undefined;
  return (ORG_SEGMENTS.has(parts[1]!) ? parts.slice(1) : parts.slice(2)).join("/");
}

function matchLen(joined: string, segments: string[]): number {
  let best = 0;
  for (const seg of segments) {
    if (joined === seg || joined.startsWith(`${seg}/`)) best = Math.max(best, seg.length);
  }
  return best;
}

/** The active section for a pathname: the most specific segment match wins. */
export function activeNav(pathname: string): NavItem | undefined {
  const joined = rest(pathname);
  if (joined === undefined) return undefined;
  let best: { item: NavItem; len: number } | undefined;
  for (const item of NAV) {
    const len = matchLen(joined, item.segments);
    if (len && (!best || len > best.len)) best = { item, len };
  }
  return best?.item;
}

/** The section's active tab for a pathname, if the page is one of its views. */
export function activeTab(item: NavItem | undefined, pathname: string, search = ""): NavTab | undefined {
  const joined = rest(pathname);
  if (!item?.tabs || joined === undefined) return undefined;
  const params = new URLSearchParams(search);
  // Legacy parallel-agent links still select Groups in the flattened navigation.
  if (item.id === "agents" && params.get("view") === "parallel") params.set("tab", "groups");
  let best: { tab: NavTab; len: number } | undefined;
  for (const t of item.tabs) {
    if (t.query && joined === t.path) {
      const value = params.get(t.query.key);
      if (value === t.query.value || (value === null && t.query.fallback)) return t;
      continue;
    }
    const len = matchLen(joined, t.segments);
    if (len && (!best || len > best.len)) best = { tab: t, len };
  }
  return best?.tab;
}

/**
 * The section's active rail child for a location. A query view is active on its list page when the param picks it
 * (or is absent, for the fallback view), and on the pages under its other segments; a plain child by its segments.
 */
export function activeChild(item: NavItem | undefined, pathname: string, search: string): NavTab | undefined {
  const joined = rest(pathname);
  if (!item?.children || joined === undefined) return undefined;
  const params = new URLSearchParams(search);
  return item.children.find((c) => {
    if (!c.query) return matchLen(joined, c.segments) > 0;
    if (joined === c.path) {
      const v = params.get(c.query.key);
      return v === c.query.value || (v === null && Boolean(c.query.fallback));
    }
    return matchLen(joined, c.segments.filter((s) => s !== c.path)) > 0;
  });
}

/** Whether the pathname is a section view itself (a tab's or child's list page), not an object or create page. */
export function isSectionView(item: NavItem | undefined, pathname: string): boolean {
  const joined = rest(pathname);
  if (!item || joined === undefined) return false;
  const paths = [item.path, ...(item.tabs ?? []).map((t) => t.path), ...(item.children ?? []).map((t) => t.path)];
  return paths.includes(joined);
}
